class SpotifyRateLimiter {
  constructor() {
    this.requestQueue = [];
    this.isProcessing = false;
    this.retryAfter = 0;
    this.requestCount = 0;
    this.windowStart = Date.now();
    this.cache = new Map();

    // Conservative limits to avoid 429 errors
    this.maxRequestsPerSecond = 8; // Spotify allows ~100/hour, being very conservative
    this.cacheExpiry = 5000; // 5 seconds cache
  }

  async makeRequest(url, options = {}) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ url, options, resolve, reject, timestamp: Date.now() });
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      // Check if we need to wait for rate limit reset
      if (this.retryAfter > Date.now()) {
        const waitTime = this.retryAfter - Date.now();
        console.log(`🚫 Rate limited, waiting ${waitTime}ms`);
        await this.sleep(waitTime);
      }

      // Check request count per window
      const now = Date.now();
      if (now - this.windowStart > 1000) {
        // Reset window
        this.requestCount = 0;
        this.windowStart = now;
      }

      if (this.requestCount >= this.maxRequestsPerSecond) {
        // Wait for window to reset
        const waitTime = 1000 - (now - this.windowStart);
        if (waitTime > 0) {
          await this.sleep(waitTime);
          continue;
        }
      }

      const request = this.requestQueue.shift();

      try {
        // Check cache first. Only GETs are cacheable (PUT/POST must always reach Spotify,
        // the cache key ignores the body), and live player state must never be stale.
        const cacheable = this.isCacheable(request.url, request.options);
        const cacheKey = this.getCacheKey(request.url, request.options);
        const cachedResponse = cacheable ? this.getFromCache(cacheKey) : null;

        if (cachedResponse) {
          console.log(`📦 Using cached response for ${request.url}`);
          request.resolve(cachedResponse);
          continue;
        }

        this.requestCount++;
        console.log(`🌐 Making API request ${this.requestCount}/${this.maxRequestsPerSecond} to ${request.url}`);

        const response = await fetch(request.url, request.options);

        if (response.status === 429) {
          console.log('🚫 Rate limited (429)');
          // Check Retry-After header
          const retryAfter = response.headers.get('Retry-After');
          this.retryAfter = Date.now() + (retryAfter ? parseInt(retryAfter) * 1000 : 5000);

          // Put request back at front of queue
          this.requestQueue.unshift(request);
          continue;
        }

        if (response.ok) {
          // Cache successful responses
          const responseData = {
            status: response.status,
            headers: Object.fromEntries(response.headers.entries()),
            json: async () => {
              if (!responseData._jsonData) {
                const text = await response.text();
                responseData._jsonData = text ? JSON.parse(text) : null;
              }
              return responseData._jsonData;
            },
            text: async () => {
              if (!responseData._textData) {
                responseData._textData = await response.text();
              }
              return responseData._textData;
            }
          };

          if (cacheable) {
            this.setCache(cacheKey, responseData);
          }
          request.resolve(responseData);
        } else {
          request.reject(new Error(`HTTP ${response.status}: ${response.statusText}`));
        }

      } catch (error) {
        console.error('Request failed:', error);
        request.reject(error);
      }

      // Small delay between requests to be extra safe
      await this.sleep(50);
    }

    this.isProcessing = false;
  }

  isCacheable(url, options) {
    const method = options.method || 'GET';
    return method === 'GET' && !url.includes('/me/player');
  }

  getCacheKey(url, options) {
    const method = options.method || 'GET';
    const headers = JSON.stringify(options.headers || {});
    return `${method}:${url}:${headers}`;
  }

  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  setCache(key, data) {
    // Clone the response for caching
    const cachedData = {
      status: data.status,
      headers: data.headers,
      _jsonData: data._jsonData,
      _textData: data._textData,
      json: data.json,
      text: data.text
    };

    this.cache.set(key, {
      data: cachedData,
      timestamp: Date.now()
    });

    // Limit cache size
    if (this.cache.size > 50) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Clear cache manually if needed
  clearCache() {
    this.cache.clear();
  }

  // Get stats for debugging
  getStats() {
    return {
      queueLength: this.requestQueue.length,
      requestCount: this.requestCount,
      cacheSize: this.cache.size,
      isProcessing: this.isProcessing,
      retryAfter: this.retryAfter > Date.now() ? this.retryAfter - Date.now() : 0
    };
  }
}

// Export singleton instance
export const spotifyRateLimiter = new SpotifyRateLimiter();
export default SpotifyRateLimiter;